"use client";

import { ArrowDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Stewardship() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const illustrations = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_blueprint_detail.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_blueprint_detail.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_blueprint_detail.webp-wvc-sizes",
    alt: "Architectural blueprint detail on a drafting table",
    rotate: "-rotate-[10deg]",
    position: "top-0 left-4 md:left-8",
    size: "w-40 md:w-56",
    delay: "delay-100"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management_key.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management_key.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management_key.png-wvc-sizes",
    alt: "Brass house key on a wooden surface",
    rotate: "rotate-[6deg]",
    position: "top-24 right-2 md:right-6",
    size: "w-48 md:w-64",
    delay: "delay-200"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_tools_set.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_tools_set.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/construction_tools_set.png-wvc-sizes",
    alt: "Professional construction tools arranged neatly",
    rotate: "-rotate-[4deg]",
    position: "bottom-2 left-12 md:left-20",
    size: "w-44 md:w-60",
    delay: "delay-300"
  }];
  return <section data-section-id="1595" id="stewardship-care" className="bg-background text-foreground relative overflow-hidden py-32 md:py-44">
      {}
      <div className="absolute top-0 left-0 right-0 h-[6px] bg-secondary" />
      <div className="absolute top-[6px] left-1/2 -translate-x-1/2 flex gap-2">
        <span className="w-2 h-2 rounded-lg bg-primary" />
        <span className="w-2 h-2 rounded-lg bg-primary" />
        <span className="w-2 h-2 rounded-lg bg-primary" />
      </div>

      <div className="max-w-[1360px] mx-auto px-6 md:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-5 relative transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div aria-hidden="true" className="pointer-events-none absolute -top-16 -left-4 font-default font-extrabold leading-none select-none" style={{
            fontSize: "clamp(10rem, 22vw, 20rem)",
            color: "var(--foreground)",
            opacity: 0.06
          }}>
              04
            </div>

            {}
            <div className="relative inline-block mb-8">
              <span className="inline-block -rotate-[6deg] bg-secondary text-secondary-foreground font-mono uppercase text-xs tracking-[0.18em] px-5 py-2 rounded-lg shadow-md" style={{
              borderRadius: "0px"
            }}>
                ◉ Property Stewardship
              </span>
            </div>

            {}
            <h1 className="font-default font-extrabold leading-[0.92] tracking-tight mb-8" style={{
            fontSize: "clamp(2.75rem, 7.4vw, 6.5rem)"
          }}>
              Built for{" "}
              <span className="relative inline-block">
                <em className="not-italic font-default font-extrabold" style={{
                color: "var(--primary)",
                fontStyle: "italic",
                display: "inline-block"
              }}>
                  lasting
                </em>
                {}
                <svg aria-hidden="true" className="absolute left-0 -bottom-3 w-full" viewBox="0 0 120 12" preserveAspectRatio="none" style={{
                height: "10px"
              }}>
                  <path d="M0 6 Q 15 0, 30 6 T 60 6 T 90 6 T 120 6" fill="none" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>{" "}
              value
            </h1>

            {}
            <p className="font-sans text-base md:text-lg leading-relaxed mb-10 max-w-xl" style={{
            color: "color-mix(in oklab, var(--foreground) 80%, transparent)"
          }}>
              At Moscariello Construction, we believe that true craftsmanship extends beyond the final nail. Our property stewardship services ensure your investment remains in peak condition, managed with the same precision and integrity we bring to every build.
            </p>

            <div className="flex flex-wrap items-center gap-6">
              <Link to="/PropertyStewardshipPage">
                <Button size="lg" className="bg-primary text-primary-foreground font-mono uppercase tracking-[0.14em] text-xs px-7 py-6 rounded-lg hover:rotate-[-2deg] hover:scale-[1.04] transition-transform duration-200" style={{
                borderRadius: "0px"
              }}>
                  Explore Services
                </Button>
              </Link>

              <Link to="/ConnectWithUsPage" className="group relative inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.14em] text-foreground">
                <span className="relative">
                  Contact our team
                  <svg aria-hidden="true" className="absolute left-0 -bottom-2 w-0 group-hover:w-full transition-all duration-[360ms] ease-out" viewBox="0 0 120 8" preserveAspectRatio="none" style={{
                  height: "6px"
                }}>
                    <path d="M0 4 Q 15 0, 30 4 T 60 4 T 90 4 T 120 4" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
                <ArrowDown className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 relative h-[420px] md:h-[560px] transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <svg aria-hidden="true" className="w-[80%] h-[80%] animate-[spin_28s_linear_infinite]" viewBox="0 0 200 200">
                <g stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" opacity="0.85">
                  {Array.from({
                  length: 14
                }).map((_, i) => {
                  const angle = i / 14 * Math.PI * 2;
                  const x1 = 100 + Math.cos(angle) * 30;
                  const y1 = 100 + Math.sin(angle) * 30;
                  const x2 = 100 + Math.cos(angle) * 70;
                  const y2 = 100 + Math.sin(angle) * 70;
                  return <line key={i} data-index={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
                })}
                </g>
                <circle cx="100" cy="100" r="20" fill="var(--secondary)" opacity="0.4" />
              </svg>
            </div>

            {}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none mix-blend-overlay" style={{
            opacity: 0.09,
            backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px), radial-gradient(var(--foreground) 1px, transparent 1px)",
            backgroundSize: "3px 3px, 5px 5px",
            backgroundPosition: "0 0, 1.5px 1.5px"
          }} />

            {}
            {illustrations.map((item, i) => <div key={i} data-index={i} className={`absolute ${item.position} ${item.size} ${item.rotate} ${item.delay} transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <div className="relative bg-card p-4 rounded-lg shadow-2xl border border-border">
                  <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-auto block" />
                </div>
              </div>)}

            {}
            <div className="absolute bottom-6 right-4 md:right-12 -rotate-[8deg] bg-primary text-primary-foreground font-mono uppercase text-[10px] tracking-[0.18em] px-4 py-2 shadow-lg z-10" style={{
            borderRadius: "0px"
          }}>
               Quality Assured
            </div>
          </div>
        </div>
      </div>
    </section>;
}